import type { ComponentProps, FC } from "react";
import styled from "@emotion/styled";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";
import { colors, radius, shadows, spacing } from "../design-tokens";

// Modal Content
const ModalContent = styled(DialogPrimitive.Content)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${colors.surface};
  border-radius: ${radius.md}px;
  ${shadows.main}
  padding: ${spacing.xl}px;
  width: 90vw;
  max-width: 500px;
  max-height: 85vh;
  overflow-y: auto;
  outline: none;
  z-index: 9999;
  
  &[data-state="open"] {
    animation: contentIn 200ms ease-out;
  }
  
  &[data-state="closed"] {
    animation: contentOut 200ms ease-in;
  }
  
  @keyframes contentIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  @keyframes contentOut {
    from {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    to {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
  }
`;

// Close Button
const ModalClose = styled(DialogPrimitive.Close)`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1/1;
  width: fit-content;
  position: absolute;
  top: ${spacing.lg}px;
  right: ${spacing.lg}px;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${spacing.xs}px;
  border-radius: ${radius.sm}px;
  color: ${colors.textSecondary};
  transition: all 200ms ease;

  &:hover {
    background: ${colors.surface};
    color: ${colors.text};
  }

  &:focus {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

// Modal Header
const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  margin-bottom: ${spacing.lg}px;
`;

// Modal Title
const ModalTitle = styled(DialogPrimitive.Title)`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.primary};
  margin: 0;
  padding-right: ${spacing.xl}px; /* Make space for close button */
`;

// Modal Description
const ModalDescription = styled(DialogPrimitive.Description)`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

// Modal Footer
const ModalFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  margin-top: ${spacing.lg}px;
  
  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

// Portal component that handles shadow DOM
function ModalPortal({
  children,
  container,
  ...props
}: ComponentProps<typeof DialogPrimitive.Portal> & {
  container?: Element | null;
}) {
  if (!container) {
    return <>{children}</>;
  }
  return (
    <DialogPrimitive.Portal container={container} {...props}>
      {children}
    </DialogPrimitive.Portal>
  );
}

// Export all primitives
export const Modal = DialogPrimitive.Root;
export const ModalTrigger = DialogPrimitive.Trigger;

// Styled overlay component for web components (not using Radix Overlay)
const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 9998;
  /* Ensure clicks are registered in web components */
  pointer-events: auto;
  touch-action: none;
  
  &[data-state="open"] {
    animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes overlayShow {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Custom overlay component for web components (doesn't use Radix primitives)
export const CustomModalOverlay: FC<{
  isOpen?: boolean;
  onClick?: () => void;
}> = ({ isOpen = true, onClick, ...props }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <StyledOverlay
      onClick={handleClick}
      data-state={isOpen ? "open" : "closed"}
      {...props}
    />
  );
};

// Legacy Radix-based overlay for backward compatibility
const ModalOverlayStyled = styled(DialogPrimitive.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  pointer-events: auto;
  touch-action: none;
  z-index: 9998;
  
  &[data-state="open"] {
    animation: overlayIn 200ms ease-out;
  }
  
  &[data-state="closed"] {
    animation: overlayOut 200ms ease-in;
  }
  
  @keyframes overlayIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes overlayOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;

// Enhanced ModalOverlay that handles click events properly in web components
export const ModalOverlay: FC<ComponentProps<typeof DialogPrimitive.Overlay> & {
  onClick?: () => void;
}> = ({ onClick, ...props }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  return <ModalOverlayStyled onClick={handleClick} {...props} />;
};

export { ModalClose, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalPortal, ModalTitle };

// Close Icon Component
export const CloseIcon = () => <X size={20} />;

// Composed Modal Content with Overlay (commonly used together)
export const ModalContentWithOverlay: FC<
  ComponentProps<typeof ModalContent> & {
    showCloseButton?: boolean;
    container?: Element | null;
  }
> = ({ children, showCloseButton = true, container, ...props }) => (
  <ModalPortal container={container}>
    <ModalOverlayStyled />
    <ModalContent {...props}>
      {children}
      {showCloseButton && (
        <ModalClose>
          <CloseIcon />
        </ModalClose>
      )}
    </ModalContent>
  </ModalPortal>
);
